import type { User } from '@auth0/auth0-angular';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
} from 'rxjs/operators';
import {
  CustomerAccessLevelEnum,
  CustomerInfoDto,
  CustomersClient,
} from '@/app/core/auto-generated/api.service';
import { SessionStore } from '@/app/core/state/session-store';
import { UserInfo } from '@/app/core/state/session.claims';
import { PosthogService } from './posthog.service';

type AuthenticatedUser = User & {
  email: string;
};

@Injectable({ providedIn: 'root' })
export class PosthogIdentityService {
  private readonly authService = inject(AuthService);
  private readonly customersClient = inject(CustomersClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly posthogService = inject(PosthogService);
  private readonly session = inject(SessionStore);
  private identifiedEmail: string | null = null;

  initialize(): void {
    this.authService.isAuthenticated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isAuthenticated) => {
        if (!isAuthenticated && this.identifiedEmail) {
          this.posthogService.reset();
          this.identifiedEmail = null;
        }
      });

    combineLatest([this.authService.isAuthenticated$, this.authService.user$])
      .pipe(
        filter((context) => isAuthenticatedUserContext(context)),
        map(([, user]) => user),
        distinctUntilChanged((previous, current) => {
          return previous.email === current.email;
        }),
        switchMap((user) => this.buildIdentityContext(user)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((context) => {
        this.identifyUser(context.user, context.userInfo, context.customer);
      });
  }

  private buildIdentityContext(user: AuthenticatedUser): Observable<{
    customer: CustomerInfoDto | undefined;
    user: AuthenticatedUser;
    userInfo: UserInfo;
  }> {
    return this.session.userInfo$.pipe(
      take(1),
      switchMap((userInfo) =>
        this.getPosthogCustomer(userInfo).pipe(
          map((customer) => ({ customer, user, userInfo }))
        )
      ),
      catchError((error: unknown) => {
        this.posthogService.captureException(error, {
          source: 'posthog.identity',
        });
        return EMPTY;
      })
    );
  }

  private getPosthogCustomer(
    userInfo: UserInfo
  ): Observable<CustomerInfoDto | undefined> {
    if (!userInfo.CustomerId) {
      return of(undefined);
    }

    return this.customersClient
      .customers_GetUserCustomers(CustomerAccessLevelEnum.View)
      .pipe(
        map((customers) =>
          customers?.find((customer) => customer.id === userInfo.CustomerId)
        ),
        catchError((error: unknown) => {
          this.posthogService.captureException(error, {
            source: 'posthog.customerContext',
          });
          return of(undefined);
        })
      );
  }

  private identifyUser(
    user: AuthenticatedUser,
    userInfo: UserInfo,
    customer: CustomerInfoDto | undefined
  ): void {
    this.identifiedEmail = user.email;

    this.posthogService.identify(user.email, {
      name: user.name ?? userInfo.fullname,
      email: user.email,
      customer_id: userInfo.CustomerId,
      customer_name: customer?.name,
      is_asm: userInfo.IsASM,
    });

    if (userInfo.CustomerId) {
      this.posthogService.group('customer', userInfo.CustomerId.toString(), {
        name: customer?.name ?? userInfo.CustomerId.toString(),
        customer_id: userInfo.CustomerId,
        external_customer_id: customer?.externalCustomerId,
      });
    }
  }
}

function isAuthenticatedUserContext(
  context: [boolean, User | null | undefined]
): context is [true, AuthenticatedUser] {
  const [isAuthenticated, user] = context;

  return isAuthenticated && !!user?.email;
}
